/**
 * Subscription access provider. There is no free trial: full access needs a
 * membership. Trials granted before launch keep working until they end.
 * Settings stays reachable unpaid (allowUnpaid) for sign-out and deletion.
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import StartupSurface from "../frontrow/StartupSurface";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AccessContext = createContext({ loading: true, tier: "individual", entitled: true });
const OPEN_REASONS = new Set(["internal_trial", "trialing", "active", "past_due", "grandfathered", "billing_lookup_failed_open", "enforcement_off"]);

const daysUntil = (iso) => {
  if (!iso) return null;
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  return Math.max(0, days);
};

// Last server answer for the signed-in user and family, reused across route
// changes so navigation doesn't flash the startup screen. Every mount still
// re-checks with the server, so a stale result never outlives one request.
let cachedAccess = null;
export const clearEntitlementCache = () => { cachedAccess = null; };

const toAccess = (data) => ({
  loading: false,
  tier: data.plan?.startsWith("family") ? "family" : "individual",
  entitled: data.entitled === true && OPEN_REASONS.has(data.reason),
  ...data,
});

export const EntitlementGate = ({ children, allowUnpaid = false }) => {
  const { familyId, clerkUser } = useAuth();
  const key = `${clerkUser?.id || ""}|${familyId || ""}`;
  const [access, setAccess] = useState(() =>
    cachedAccess?.key === key ? cachedAccess.access : { loading: true, tier: "individual", entitled: true });

  useEffect(() => {
    let alive = true;
    if (cachedAccess && cachedAccess.key !== key) cachedAccess = null;
    axios.get(`${API}/billing/status`)
      .then((r) => {
        if (!alive) return;
        const next = toAccess(r.data || {});
        cachedAccess = { key, access: next };
        setAccess(next);
      })
      .catch(() => {
        if (!alive) return;
        // Keep a known server answer; only a first check falls back open.
        if (cachedAccess?.key === key) return;
        setAccess({ loading: false, tier: "individual", entitled: true, reason: "billing_lookup_failed_open" });
      });
    return () => { alive = false; };
  }, [key]);

  if (access.loading) {
    return <StartupSurface testId="entitlement-loading" />;
  }

  if (!access.entitled && !allowUnpaid && (access.reason === "membership_required" || access.reason === "trial_not_started")) {
    return <AccessContext.Provider value={access}><MembershipRequired /></AccessContext.Provider>;
  }

  if (!access.entitled && !allowUnpaid) {
    const retainedDays = daysUntil(access.retention_until);
    return (
      <AccessContext.Provider value={access}>
        <div data-testid="trial-expired-screen" className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-5">
          <div className="max-w-md w-full bg-white rounded-3xl border border-[#E6E8EB] fr-shadow p-6">
            <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#7A7D7B]">FrontRow paused</p>
            <h1 className="mt-2 text-[23px] font-extrabold text-[#172033]">{access.reason === "trial_expired" ? "Your trial has ended" : "Your membership has ended"}</h1>
            <p className="mt-3 text-[14px] leading-relaxed text-[#4A5568]">
              Email processing and notifications are paused. Your existing FrontRow information is being kept securely{retainedDays !== null ? ` for another ${retainedDays} day${retainedDays === 1 ? "" : "s"}` : " for 90 days"}.
            </p>
            {access.retention_until && <p className="mt-2 text-[12px] text-[#7A7D7B]">Scheduled deletion: {new Date(access.retention_until).toLocaleDateString()}</p>}
            <Link to="/billing" className="mt-5 block w-full rounded-full bg-[#172033] text-white py-3 text-center text-[14px] font-semibold">Choose a plan</Link>
            <p className="mt-3 text-[12px] leading-relaxed text-[#7A7D7B]">When you subscribe, FrontRow reconnects and catches up from where it stopped or the previous 21 days, whichever is shorter.</p>
          </div>
        </div>
      </AccessContext.Provider>
    );
  }

  return (
    <AccessContext.Provider value={access}>
      <div data-frontrow-tier={access.tier}>
        {access.reason === "internal_trial" && access.trial_days_remaining <= 7 && (
          <div data-testid="trial-reminder-banner" className="flex items-center justify-between gap-3 bg-[#FFF7EC] border-b border-[#F0DFC7] px-4 py-2 text-[12px] text-[#5C492F]">
            <span><strong>{access.trial_days_remaining} day{access.trial_days_remaining === 1 ? "" : "s"} left</strong> in your FrontRow trial.</span>
            <Link to="/billing">See plans</Link>
          </div>
        )}
        {children}
      </div>
    </AccessContext.Provider>
  );
};

// New accounts: purchase or restore first. Settings keeps sign-out and deletion.
const MembershipRequired = () => (
  <div data-testid="membership-required-screen" className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-5">
    <div className="max-w-md w-full bg-white rounded-3xl border border-[#E6E8EB] fr-shadow p-6">
      <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#7A7D7B]">Welcome to FrontRow</p>
      <h1 className="mt-2 text-[23px] font-extrabold text-[#172033]">Choose a membership to get started</h1>
      <p className="mt-3 text-[14px] leading-relaxed text-[#4A5568]">
        Pick Individual or Family to start turning school emails into a clear family dashboard. Already a member? Restore your purchase from the membership page.
      </p>
      <Link to="/billing" data-testid="membership-required-plans" className="mt-5 block w-full rounded-full bg-[#172033] text-white py-3 text-center text-[14px] font-semibold">See memberships</Link>
      <Link to="/settings" data-testid="membership-required-settings" className="mt-3 block w-full rounded-full border border-[#E6E8EB] py-3 text-center text-[14px] font-semibold text-[#172033]">Account settings</Link>
    </div>
  </div>
);

export const useEntitlement = () => useContext(AccessContext);
